import { PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useResourceAuthoring } from './AuthoringContext';
import { AUTHORING_LABEL, type AuthorableItem, type AuthoringKind } from './types';

interface CreateResourceButtonProps {
    kind: AuthoringKind;
    className?: string;
}

/**
 * The section's only "add" affordance, rendered by each list page. Returns null
 * for a reader, so a page can place it unconditionally.
 */
export const CreateResourceButton: React.FC<CreateResourceButtonProps> = ({ kind, className }) => {
    const { canEdit, startCreate } = useResourceAuthoring();
    if (!canEdit) return null;

    return (
        <button
            type="button"
            onClick={() => startCreate(kind)}
            className={`h-9 inline-flex items-center gap-1.5 px-3.5 rounded-lg bg-gray-800 text-white text-sm font-semibold hover:bg-gray-900 transition-colors ${className ?? ''}`}
        >
            <PlusIcon className="w-4 h-4" />
            New {AUTHORING_LABEL[kind]}
        </button>
    );
};

interface EditResourceButtonProps {
    kind: AuthoringKind;
    item: AuthorableItem;
    /** What the row is called, for the button's accessible name. */
    name: string;
    className?: string;
}

/**
 * Per-row edit control. Deliberately icon-only and quiet: on a reader's screen
 * it is absent, and on an author's it must not compete with Open or Preview.
 */
export const EditResourceButton: React.FC<EditResourceButtonProps> = ({
    kind,
    item,
    name,
    className
}) => {
    const { canEdit, startEdit } = useResourceAuthoring();
    if (!canEdit) return null;

    return (
        <button
            type="button"
            onClick={() => startEdit(kind, item)}
            aria-label={`Edit ${name}`}
            title={`Edit ${name}`}
            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors ${className ?? ''}`}
        >
            <PencilSquareIcon className="w-4 h-4" />
        </button>
    );
};
